// Règles de déplacement du joueur, communes au serveur (qui valide chaque pas) et au client (qui le prédit) : case
// libre, surf, glace, courants, bordures à sauter, rochers à pousser. Fonctions pures, sans état.
import { ledgeLanding, objectAt, type CellIndex, type MapElement, type MapObject } from './map-elements.ts';
import { DIRECTION_VECTORS, facingCell, type Direction, type Position } from './protocol.ts';

/** La carte telle que ce joueur la voit, pour décider de ses pas. */
export interface MoveWorld {
  width: number;
  height: number;
  /** Collisions vues par le joueur (obstacles qu'il a dégagés en moins, rochers où il les a poussés). */
  cells: CellIndex;
  /** Cases de glace (zones à effets « glissade »). */
  slides: ReadonlySet<number>;
  /** Sens du courant des cases des zones à effets « courant ». */
  currents: ReadonlyMap<number, Direction>;
  /** Objets posés vus par le joueur et éléments, pour reconnaître un rocher à pousser. */
  objects: readonly MapObject[];
  elements: ReadonlyMap<number, MapElement>;
  /** Case prise par un PNJ, un coffre ou un monstre posé (tels que ce joueur les voit). */
  occupied(position: Position): boolean;
}

/** Le joueur qui se déplace. */
export interface Mover extends Position {
  dir: Direction;
  /** Il surfe : il avance aussi dans l'eau. */
  surfing: boolean;
  /** Il porte des patins : il ne glisse pas sur la glace. */
  skates: boolean;
}

/**
 * Durée d'une poussée de rocher (animation du rocher d'une case à la suivante) : le joueur ne peut pas en relancer une
 * avant la fin, même en gardant la touche enfoncée (le serveur vérifie aussi).
 */
export const PUSH_MS = 350;

/** Ce que donne un pas dans un sens. */
export type StepPlan =
  /** Il va sur `target` (case voisine, ou de l'autre côté d'une bordure) ; `dismount` : il reprend pied en sortant de l'eau. */
  | { kind: 'move'; target: Position; sliding: boolean; dismount: boolean }
  /** Contre un rocher à pousser (en `target`) : s'il bouge, c'est le serveur qui le dit. */
  | { kind: 'push'; target: Position }
  /** Case prise : il se tourne seulement. */
  | { kind: 'blocked'; target: Position }
  /** Sur la glace, il glisse tout droit (sur un courant, il suit le courant) : impossible d'aller ailleurs tant que la case devant est libre. */
  | { kind: 'locked' };

export const inBounds = (world: Pick<MoveWorld, 'width' | 'height'>, { x, y }: Position): boolean =>
  x >= 0 && y >= 0 && x < world.width && y < world.height;

const indexOf = (world: Pick<MoveWorld, 'width'>, { x, y }: Position) => y * world.width + x;

/** On surfe sur cette case (eau surfable, dégagée de tout objet qui bloque). */
export const isSurfable = (world: Pick<MoveWorld, 'width' | 'height' | 'cells'>, position: Position): boolean =>
  inBounds(world, position) && world.cells.surf.has(indexOf(world, position));

/** Le joueur glisse-t-il sur cette case (glace, sauf avec des patins) ? */
export const slidesAt = (world: Pick<MoveWorld, 'width' | 'height' | 'slides'>, mover: Pick<Mover, 'skates'>, position: Position): boolean =>
  !mover.skates && inBounds(world, position) && world.slides.has(indexOf(world, position));

/** Sens du courant de cette case (null : pas de courant ; les patins n'y changent rien). */
export const currentAt = (world: Pick<MoveWorld, 'width' | 'height' | 'currents'>, position: Position): Direction | null =>
  (inBounds(world, position) && world.currents.get(indexOf(world, position))) || null;

/** Le joueur est-il emporté sur cette case (glace ou courant) ? Ses pas y sont ceux d'une glissade. */
export const carriedAt = (world: Pick<MoveWorld, 'width' | 'height' | 'slides' | 'currents'>, mover: Pick<Mover, 'skates'>, position: Position): boolean =>
  slidesAt(world, mover, position) || currentAt(world, position) !== null;

/** Le joueur peut-il aller sur cette case : dans la carte, libre de toute collision (ou de l'eau s'il surfe), sans PNJ, coffre ni monstre posé ? */
export function canEnter(world: MoveWorld, mover: Pick<Mover, 'surfing'>, target: Position): boolean {
  if (!inBounds(world, target) || world.occupied(target)) return false;
  return !world.cells.blocked.has(indexOf(world, target)) || (mover.surfing && isSurfable(world, target));
}

/** Rocher à pousser qui bloque cette case. */
export function isPushable(world: MoveWorld, target: Position): boolean {
  if (!inBounds(world, target) || !world.cells.blocked.has(indexOf(world, target))) return false;
  const object = objectAt(world.objects, world.elements, target.x, target.y);
  return object !== undefined && world.elements.get(object.elementId)?.obstacle === 'push';
}

/**
 * Le pas imposé : sur un courant, dans son sens ; sur la glace, tout droit ; tant que la case devant est libre (null : il
 * est libre de ses pas).
 */
export function forcedSlide(world: MoveWorld, mover: Mover): Direction | null {
  const dir = currentAt(world, mover) ?? (slidesAt(world, mover, mover) ? mover.dir : null);
  return dir && canEnter(world, mover, facingCell(mover, dir)) ? dir : null;
}

/** Ce que fait un pas dans le sens `dir` (le joueur se tourne dans ce sens, sauf `locked`). */
export function planStep(world: MoveWorld, mover: Mover, dir: Direction): StepPlan {
  const forced = forcedSlide(world, mover);
  if (forced && dir !== forced) return { kind: 'locked' };
  const [dx, dy] = DIRECTION_VECTORS[dir];
  // Devant une bordure à sauter dans ce sens : on atterrit de l'autre côté (si la case d'arrivée est libre).
  const target = ledgeLanding(world.cells, world.width, mover, dir) ?? { x: mover.x + dx, y: mover.y + dy };
  if (canEnter(world, mover, target)) {
    return { kind: 'move', target, sliding: carriedAt(world, mover, mover) || carriedAt(world, mover, target), dismount: mover.surfing && !isSurfable(world, target) };
  }
  return isPushable(world, target) ? { kind: 'push', target } : { kind: 'blocked', target };
}
